// Comp-Off tab on the Leaves page: raise a compensatory-leave credit request, approve / reject the ones waiting for
// you (manager first, then Centre HR / HR Head / Admin), and follow your own requests. Server: routes/compOff.js.
import React, { useEffect, useState, useCallback } from "react";
import { Plus, Check, X, Eye } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Cd, Av, Tbl } from "../components";
import { notify, promptDialog } from "../notify";
import { fmtDMY } from "../dateFormat";

const STATUS = {
  Pending: { label: "Waiting for manager", c: K.a },
  "Mgr OK": { label: "Waiting for HR", c: K.bl },
  Approved: { label: "Approved", c: K.g },
  Rejected: { label: "Rejected", c: K.r },
  Cancelled: { label: "Cancelled", c: K.t3 },
};
const ACTION_LABEL = { submitted: "Requested", manager_approved: "Approved by manager", approved: "Approved & credited", rejected: "Rejected", cancelled: "Cancelled" };
const DAY_OPTIONS = [0.5, 1, 1.5, 2, 2.5, 3];
const today = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);

const StatusBadge = ({ s }) => { const m = STATUS[s] || { label: s, c: K.t3 }; return <Bg c={m.c} dot sm>{m.label}</Bg>; };

const Modal = ({ title, onClose, children, width = 440 }) => (
  <div className="fixed inset-0 z-[700] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div className="w-full bg-bg1 border border-b0 rounded-2xl shadow-xl p-5" style={{ maxWidth: width }} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[15px] font-extrabold text-t0">{title}</div>
        <button onClick={onClose} className="w-[28px] h-[28px] rounded-full bg-bg2 border-0 cursor-pointer flex items-center justify-center text-t2"><X size={15} /></button>
      </div>
      {children}
    </div>
  </div>
);

export const CompOffPanel = ({ onRefresh, hashQuery }) => {
  const [mine, setMine] = useState([]);
  const [toApprove, setToApprove] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ work_date: today(), days: 1, reason: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      api.get("/api/comp-off?view=mine").catch(() => ({ data: [] })),
      api.get("/api/comp-off?view=approve").catch(() => ({ data: [] })),
      api.get("/api/comp-off?view=team").catch(() => ({ data: [] })),
    ]).then(([a, b, c]) => { setMine(a.data || []); setToApprove(b.data || []); setTeam(c.data || []); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback((id) => {
    api.get(`/api/comp-off/${id}`).then(setDetail).catch((e) => notify(e.message || "Couldn't open this request", { title: "Error", variant: "error" }));
  }, []);
  useEffect(() => { if (hashQuery?.tab === "compoff" && hashQuery?.id) openDetail(hashQuery.id); }, [hashQuery?.id]); // eslint-disable-line

  const submit = async () => {
    setErr("");
    if (form.reason.trim().length < 5) { setErr("Please give a reason (at least 5 characters)."); return; }
    setSaving(true);
    try {
      await api.post("/api/comp-off", { work_date: form.work_date, days: Number(form.days), reason: form.reason.trim() });
      notify("Your comp-off request was sent for approval", { title: "Requested", variant: "success" });
      setShowForm(false); setForm({ work_date: today(), days: 1, reason: "" });
      load();
    } catch (e) { setErr(e.message || "Couldn't send the request"); }
    finally { setSaving(false); }
  };

  const decide = async (r, kind) => {
    const comment = await promptDialog(kind === "approve" ? `Approve ${r.employee_name}'s comp-off (${r.days}d)? You can add a note.` : `Why are you rejecting ${r.employee_name}'s comp-off request?`,
      { title: kind === "approve" ? "Approve comp-off" : "Reject comp-off", placeholder: kind === "approve" ? "Note (optional)" : "Reason (required)", okLabel: kind === "approve" ? "Approve" : "Reject", multiline: true, required: kind !== "approve", variant: kind === "approve" ? "info" : "danger" });
    if (comment === null || comment === undefined || comment === false) return;
    if (kind === "reject" && String(comment).trim().length < 3) { notify("A reason is needed to reject.", { title: "Reason required", variant: "error" }); return; }
    setBusyId(r.id);
    try {
      const res = await api.put(`/api/comp-off/${r.id}/${kind}`, { comment: String(comment).trim() });
      notify(kind === "reject" ? "Request rejected" : res.status === "Approved" ? `Approved — ${res.credited} day(s) credited` : "Approved — sent on to HR", { title: "Saved", variant: "success" });
      setDetail(null); await load(); if (res.status === "Approved" && onRefresh) onRefresh();
    } catch (e) { notify(e.message || "Couldn't save", { title: "Error", variant: "error" }); }
    finally { setBusyId(null); }
  };

  const cancel = async (r) => {
    setBusyId(r.id);
    try { await api.put(`/api/comp-off/${r.id}/cancel`, {}); notify("Request cancelled", { title: "Saved", variant: "success" }); setDetail(null); load(); }
    catch (e) { notify(e.message || "Couldn't cancel", { title: "Error", variant: "error" }); }
    finally { setBusyId(null); }
  };

  const waiting = (s) => s === "Pending" || s === "Mgr OK";
  const empCol = { k: "e", l: "Employee", r: (r) => <div className="flex items-center gap-[6px]"><Av nm={r.employee_name} sz={22} /><span className="text-[14px] font-bold text-t0">{r.employee_name}</span></div> };
  const common = [
    { k: "w", l: "Worked on", r: (r) => <span className="text-[13px] font-semibold text-t0 whitespace-nowrap">{fmtDMY(r.work_date)}</span> },
    { k: "d", l: "Days", r: (r) => <span className="inline-block px-[8px] py-[2px] rounded-md bg-bg2 font-bold text-t1 text-[13px]">{r.days}d</span> },
    { k: "re", l: "Reason", r: (r) => <span className="text-[13px] text-t1 max-w-[240px] truncate inline-block">{r.reason}</span> },
    { k: "s", l: "Status", r: (r) => <StatusBadge s={r.status} /> },
  ];
  const approveCol = { k: "a", l: "Actions", r: (r) => <div className="flex gap-[4px]" onClick={(e) => e.stopPropagation()}>
    <Bt v="ghost" s="xs" icon={Eye} onClick={() => openDetail(r.id)} />
    {waiting(r.status) && <Bt v="success" s="xs" icon={Check} disabled={busyId === r.id} onClick={() => decide(r, "approve")}>Approve</Bt>}
    {waiting(r.status) && <Bt v="danger" s="xs" icon={X} disabled={busyId === r.id} onClick={() => decide(r, "reject")}>Reject</Bt>}
  </div> };
  const teamOnly = team.filter((r) => !toApprove.some((x) => x.id === r.id));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="text-[13px] text-t3 max-w-[640px]">Worked on a day off or a holiday? Ask for compensatory leave. It goes to your manager first, then to HR; once approved the days are added to your compensatory leave balance.</div>
        <Bt icon={Plus} onClick={() => { setErr(""); setShowForm(true); }}>Request Comp-Off</Bt>
      </div>

      {toApprove.length > 0 && (
        <div className="mb-5">
          <div className="text-[14px] font-bold text-t0 mb-2">Waiting for your approval <span className="ml-1 px-[7px] py-[1px] rounded-full text-[11px] font-extrabold" style={{ background: K.pg, color: K.p }}>{toApprove.length}</span></div>
          <Tbl cols={[empCol, ...common.slice(0, 3), { k: "s", l: "Stage", r: (r) => <StatusBadge s={r.status} /> }, approveCol]} data={toApprove} onRowClick={(r) => openDetail(r.id)} />
        </div>
      )}

      <div className="mb-5">
        <div className="text-[14px] font-bold text-t0 mb-2">My comp-off requests</div>
        {mine.length > 0 ? (
          <Tbl cols={[...common, { k: "a", l: "Actions", r: (r) => <div className="flex gap-[4px]" onClick={(e) => e.stopPropagation()}>
            <Bt v="ghost" s="xs" icon={Eye} onClick={() => openDetail(r.id)}>View</Bt>
            {waiting(r.status) && <Bt v="danger" s="xs" icon={X} disabled={busyId === r.id} onClick={() => cancel(r)}>Cancel</Bt>}
          </div> }]} data={mine} onRowClick={(r) => openDetail(r.id)} />
        ) : <div className="text-center p-[24px] text-t3 text-[14px]">{loading ? "Loading…" : "You haven't raised a comp-off request yet."}</div>}
      </div>

      {teamOnly.length > 0 && (
        <div>
          <div className="text-[14px] font-bold text-t0 mb-2">Team comp-off requests</div>
          <Tbl cols={[empCol, ...common, { k: "a", l: "", r: (r) => <Bt v="ghost" s="xs" icon={Eye} onClick={() => openDetail(r.id)} /> }]} data={teamOnly} onRowClick={(r) => openDetail(r.id)} />
        </div>
      )}

      {showForm && (
        <Modal title="Request compensatory leave" onClose={() => setShowForm(false)}>
          <label className="block text-[12px] font-semibold text-t3 uppercase tracking-wide mb-1">Date you worked</label>
          <input type="date" max={today()} value={form.work_date} onChange={(e) => setForm({ ...form, work_date: e.target.value })} className="w-full mb-3 px-3 py-2 bg-bg2 border border-b0 rounded-lg text-[14px] text-t0 outline-none" />
          <label className="block text-[12px] font-semibold text-t3 uppercase tracking-wide mb-1">Days of comp-off</label>
          <select value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className="w-full mb-3 px-3 py-2 bg-bg2 border border-b0 rounded-lg text-[14px] text-t0 outline-none">
            {DAY_OPTIONS.map((d) => <option key={d} value={d}>{d} day{d === 1 ? "" : "s"}</option>)}
          </select>
          <label className="block text-[12px] font-semibold text-t3 uppercase tracking-wide mb-1">Reason</label>
          <textarea rows={4} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="What did you work on, and why was it on a day off?" className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-lg text-[14px] text-t0 outline-none resize-none" />
          {err && <div className="text-[12px] text-r mt-1">{err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <Bt v="secondary" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Bt>
            <Bt onClick={submit} disabled={saving}>{saving ? "Sending…" : "Send for approval"}</Bt>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={`Comp-off request — ${detail.employee_name}`} onClose={() => setDetail(null)} width={520}>
          <div className="grid grid-cols-2 gap-2 mb-3 text-[13px]">
            <div className="bg-bg2 rounded-lg p-2 border border-b0"><div className="text-[11px] text-t3 uppercase">Worked on</div><div className="font-semibold text-t0">{fmtDMY(detail.work_date)}</div></div>
            <div className="bg-bg2 rounded-lg p-2 border border-b0"><div className="text-[11px] text-t3 uppercase">Days</div><div className="font-semibold text-t0">{detail.days}</div></div>
            <div className="bg-bg2 rounded-lg p-2 border border-b0"><div className="text-[11px] text-t3 uppercase">Status</div><StatusBadge s={detail.status} /></div>
            <div className="bg-bg2 rounded-lg p-2 border border-b0"><div className="text-[11px] text-t3 uppercase">Reporting manager</div><div className="font-semibold text-t0">{detail.manager_name || "—"}</div></div>
          </div>
          <div className="text-[11px] text-t3 uppercase mb-1">Reason</div>
          <div className="text-[13px] text-t0 bg-bg2 border border-b0 rounded-lg p-2 mb-3 whitespace-pre-wrap">{detail.reason}</div>
          <div className="text-[11px] text-t3 uppercase mb-1">Timeline</div>
          <div className="space-y-1 mb-3 max-h-[180px] overflow-y-auto">
            {(detail.events || []).map((ev) => (
              <div key={ev.id} className="text-[12.5px] text-t1"><b>{ACTION_LABEL[ev.action] || ev.action}</b> · {ev.actor_name || "—"} · <span className="text-t3">{fmtDMY(ev.created_at)}</span>{ev.comment ? <div className="text-t2 pl-2 border-l-2 border-b0 ml-1">{ev.comment}</div> : null}</div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            {detail.can_act && <Bt v="danger" icon={X} disabled={busyId === detail.id} onClick={() => decide(detail, "reject")}>Reject</Bt>}
            {detail.can_act && <Bt v="success" icon={Check} disabled={busyId === detail.id} onClick={() => decide(detail, "approve")}>Approve</Bt>}
            <Bt v="secondary" onClick={() => setDetail(null)}>Close</Bt>
          </div>
        </Modal>
      )}
    </div>
  );
};
